import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  type Signal,
} from '@angular/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { DAY_MONTH_YEAR_FORMAT } from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { buildEchartsTheme } from '../echarts-theme';

export interface IEchartsStackedAreaDatum {
  date: string;
  series: Record<string, number>;
}

@Component({
  selector: 'pr-echarts-stacked-area',
  templateUrl: './echarts-stacked-area.component.html',
  styleUrls: ['./echarts-stacked-area.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgxEchartsDirective],
})
export class EchartsStackedAreaComponent {
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly data = input<IEchartsStackedAreaDatum[]>([]);
  readonly seriesLabels = input<string[]>([]);
  readonly palette = input<string[]>();
  readonly label = input.required<string>();
  readonly height = input<number>(280);
  readonly ariaLabelOverride = input<string>();

  readonly hasData = computed(() => this.data().length > 0);

  readonly ariaLabel = computed(() => {
    const override = this.ariaLabelOverride();
    if (override) {
      return override;
    }
    const points = this.data();
    const labels = this.seriesLabels();
    if (points.length === 0) {
      return this.label();
    }
    return `${this.label()} stacked area chart, ${
      points.length
    } data points, series: ${labels.join(', ')}`;
  });

  readonly chartOptions: Signal<EChartsOption> = computed(() => {
    const points = this.data();
    const labels = this.seriesLabels();
    if (points.length === 0 || labels.length === 0) {
      return {};
    }

    const theme = buildEchartsTheme(this._elementRef.nativeElement);
    const customPalette = this.palette();
    const dates = points.map((point) =>
      moment(point.date).format(DAY_MONTH_YEAR_FORMAT)
    );

    const series = labels.map((seriesLabel, index) => ({
      type: 'line' as const,
      name: seriesLabel,
      stack: 'total',
      areaStyle: { opacity: 0.7 },
      smooth: true,
      symbol: 'none',
      data: points.map((point) => point.series[seriesLabel] ?? 0),
      ...(customPalette?.[index]
        ? { itemStyle: { color: customPalette[index] } }
        : {}),
    }));

    return {
      ...theme,
      ...(customPalette ? { color: customPalette } : {}),
      grid: {
        ...theme.grid,
        top: 56,
      },
      legend: {
        ...theme.legend,
        top: 0,
        padding: [0, 0, 8, 0],
      },
      xAxis: {
        ...(theme.xAxis as Record<string, unknown>),
        type: 'category',
        data: dates,
        boundaryGap: false,
      },
      yAxis: {
        ...(theme.yAxis as Record<string, unknown>),
        type: 'value',
      },
      series,
    };
  });
}
